import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Tag, Spin, Empty, Image, Divider, Button, Input, DatePicker, Upload, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined, CameraOutlined } from '@ant-design/icons';
import { getVechanPaymentLogApi, addVechanPaymentLogApi, deleteVechanPaymentLogApi, updateVechanPaymentImageApi } from '../../api/sales';
import { formatCurrency, formatDate, getPaymentStatusColor } from '../../utils/formatters';
import { PAYMENT_LOG_IMAGE } from '../../utils/constants';
import dayjs from 'dayjs';

const STATUS_LABEL = { credit: 'Received', partial: 'Partial', debit: 'Outstanding' };

const EMPTY_ADD = { amount: '', date: dayjs(), remark: '', image: null };

function SummaryRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
      <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 15, color: color || '#222' }}>{value}</span>
    </div>
  );
}

export default function SaleDetailModal({ open, onClose, sale }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [addOpen, setAddOpen]         = useState(false);
  const [addForm, setAddForm]         = useState(EMPTY_ADD);
  const [addLoading, setAddLoading]   = useState(false);
  const [deletingId, setDeletingId]   = useState(null);
  const [imgLoadingId, setImgLoadingId] = useState(null);

  const fetchLog = useCallback(() => {
    if (!sale?.id) return;
    setLoading(true);
    getVechanPaymentLogApi({ vechan_id: sale.id })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sale?.id]);

  useEffect(() => {
    if (!open || !sale?.id) return;
    setData(null);
    setAddOpen(false);
    setAddForm(EMPTY_ADD);
    fetchLog();
  }, [open, sale?.id, fetchLog]);

  const handleAdd = async () => {
    if (!addForm.amount || !addForm.date) { message.warning('Enter amount and date'); return; }
    if (Number(addForm.amount) > Number(summary.remaining_amount)) {
      message.warning(`Amount cannot exceed remaining ${formatCurrency(summary.remaining_amount)}`);
      return;
    }
    setAddLoading(true);
    const fd = new FormData();
    fd.append('vechan_id', sale.id);
    fd.append('amount', addForm.amount);
    fd.append('payment_date', addForm.date.format('YYYY-MM-DD'));
    if (addForm.remark) fd.append('note', addForm.remark);
    if (addForm.image) fd.append('image', addForm.image);
    try {
      await addVechanPaymentLogApi(fd);
      message.success('Payment added');
      setAddOpen(false);
      setAddForm(EMPTY_ADD);
      fetchLog();
    } catch {
      message.error('Failed to add payment');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteVechanPaymentLogApi(id);
      message.success('Deleted');
      fetchLog();
    } catch {
      message.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateImage = async (id, file) => {
    setImgLoadingId(id);
    const fd = new FormData();
    fd.append('payment_log_id', id);
    fd.append('payment_image', file);
    try {
      await updateVechanPaymentImageApi(fd);
      message.success('Image updated');
      fetchLog();
    } catch {
      message.error('Failed to update image');
    } finally {
      setImgLoadingId(null);
    }
    return false;
  };

  const summary = data?.summary || {};
  const logs    = data?.logs    || [];

  return (
    <Modal title="Sale Details" open={open} onCancel={onClose} footer={null} width={460}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ background: '#fafafa', borderRadius: 10, padding: '4px 16px', marginBottom: 16 }}>
            <SummaryRow label="Total Amount" value={formatCurrency(summary.total_amount)} color="#222" />
            <Divider style={{ margin: '2px 0' }} />
            <SummaryRow label="Received"     value={formatCurrency(summary.received_amount)}  color="#52c41a" />
            <Divider style={{ margin: '2px 0' }} />
            <SummaryRow
              label="Remaining"
              value={formatCurrency(summary.remaining_amount)}
              color={summary.remaining_amount > 0 ? '#fa8c16' : '#52c41a'}
            />
            <Divider style={{ margin: '2px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <span style={{ color: '#888', fontSize: 13 }}>Status</span>
              <Tag color={getPaymentStatusColor(summary.payment_status)}>
                {STATUS_LABEL[summary.payment_status] || summary.payment_status}
              </Tag>
            </div>
          </div>

          {/* Logs header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Payment Logs</span>
            {(summary.payment_status === 'partial' || summary.payment_status === 'debit') && !addOpen && (
              <Button
                size="small" type="primary" icon={<PlusOutlined />}
                style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                onClick={() => setAddOpen(true)}
              >
                Add Payment
              </Button>
            )}
          </div>

          {/* Inline add form */}
          {addOpen && (
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <Input
                    type="number" placeholder="Amount (₹)"
                    value={addForm.amount}
                    status={addForm.amount && Number(addForm.amount) > Number(summary.remaining_amount) ? 'error' : ''}
                    onChange={(e) => setAddForm((p) => ({ ...p, amount: e.target.value }))}
                  />
                  {addForm.amount && Number(addForm.amount) > Number(summary.remaining_amount) && (
                    <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 2 }}>Exceeds remaining {formatCurrency(summary.remaining_amount)}</div>
                  )}
                </div>
                <DatePicker
                  value={addForm.date}
                  onChange={(d) => setAddForm((p) => ({ ...p, date: d }))}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <Input
                  size="small" placeholder="Remark (optional)"
                  value={addForm.remark}
                  onChange={(e) => setAddForm((p) => ({ ...p, remark: e.target.value }))}
                  style={{ flex: 1 }}
                />
                <Upload
                  beforeUpload={(file) => { setAddForm((p) => ({ ...p, image: file })); return false; }}
                  maxCount={1} accept="image/*" showUploadList={false}
                >
                  <Button size="small" icon={<CameraOutlined />}>
                    {addForm.image ? addForm.image.name.slice(0, 12) + '…' : 'Photo'}
                  </Button>
                </Upload>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setAddOpen(false); setAddForm(EMPTY_ADD); }}>Cancel</Button>
                <Button
                  size="small" type="primary" loading={addLoading} onClick={handleAdd}
                  style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
                >Save</Button>
              </div>
            </div>
          )}

          {/* Logs list */}
          {logs.length === 0 ? (
            <Empty description="No payment logs" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {logs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 14px',
                  }}
                >
                  {/* Amount + date + note */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#52c41a' }}>{formatCurrency(log.amount)}</div>
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{formatDate(log.credit_at || log.payment_date)}</div>
                    {log.note && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{log.note}</div>}
                  </div>

                  {/* Image / change image */}
                  {log.image ? (
                    <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                      <Image
                        src={PAYMENT_LOG_IMAGE(log.image)} width={48} height={48}
                        style={{ objectFit: 'cover', borderRadius: 6, display: 'block' }}
                      />
                      <label
                        htmlFor={`pay-img-${log.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute', bottom: 2, right: 2, width: 18, height: 18,
                          background: 'rgba(0,0,0,0.7)', borderRadius: 3, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <CameraOutlined style={{ color: '#fff', fontSize: 10 }} />
                      </label>
                      <input
                        id={`pay-img-${log.id}`}
                        type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUpdateImage(log.id, file);
                          e.target.value = '';
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <label
                        htmlFor={`pay-img-${log.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 7px',
                          cursor: 'pointer', background: '#fff', height: 24, flexShrink: 0,
                        }}
                      >
                        {imgLoadingId === log.id ? <Spin size="small" /> : <CameraOutlined style={{ fontSize: 12 }} />}
                      </label>
                      <input
                        id={`pay-img-${log.id}`}
                        type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUpdateImage(log.id, file);
                          e.target.value = '';
                        }}
                      />
                    </>
                  )}

                  {/* Delete */}
                  <Popconfirm
                    title="Delete this payment log?"
                    onConfirm={() => handleDelete(log.id)}
                    okButtonProps={{ danger: true }}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} loading={deletingId === log.id} />
                  </Popconfirm>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
